import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2022 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { ErrorUtils } from "abweb/utils/ErrorUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
import satisfies from "semver/functions/satisfies";

// Dependencies can optionally have versions, so can be expressed as list of string or
// objects of one of more strings to versions.  Or, can be a single object with one or more
// string to version entries.
// dependsOn: [ "a", "b" ]
// dependsOn: [ "a", { "b": "1.3" } ]
// dependsOn: [ { "a": "2.4", "b": "1.3 }, "c" ]
// dependsOn: { "a": "2.4", "b": "1.3 }

// convert the flexible representation of dependencies into an array of [name, version] pairs,
// where the version is allowed to be undefined.
function normalizeDependencies(dependsOn) {
  function normalizeDependency(dep) {
    if (typeof dep === "string") {
      return [[dep, undefined]];
    } else {
      return Object.entries(dep);
    }
  }
  if (!dependsOn) {
    return [];
  }
  if (Array.isArray(dependsOn)) {
    // concatenate all lists of entries together into a single list
    return [].concat(...dependsOn.map(normalizeDependency));
  } else {
    return Object.entries(dependsOn);
  }
}

/**
 * A module that exports a Plugin with an export name of "plugin".
 */

export class PluginInitializer {
  constructor(providedPlugins) {
    _defineProperty(this, "initializedPlugins", new Map());
    _defineProperty(this, "incompatiblePlugins", new Map());
    _defineProperty(this, "providedPlugins", void 0);
    this.providedPlugins = providedPlugins ?? {};
  }
  async initialize(plugins) {
    const ordered = this.sortAndCheck(plugins);
    const orderedIds = ordered.map(plugin => plugin.id.toString()).join(",");
    PluginInitializer.LOGGER.info(`Initializing plugins in order: ${orderedIds}`);
    for (const plugin of ordered) {
      await this.initializePlugin(plugin);
    }
  }

  /**
   * Return a map of all plugins that have been loaded.
   * The map is ordered by the order in which the plugins loaded.
   */
  getLoadedPlugins() {
    return new Map(this.initializedPlugins);
  }

  /**
   * Return a map of all plugins that were prevented from loading.
   * The map is ordered by the order in which the plugins loaded.
   */
  getIncompatiblePlugins() {
    return new Map(this.incompatiblePlugins);
  }
  async initializePlugin(plugin) {
    PluginInitializer.LOGGER.info(`Initializing plugin ${plugin.id.toString()}`);
    await plugin.initialize();

    // track that this plugin was loaded
    this.initializedPlugins.set(plugin.id, plugin);
  }

  // verify that the given dependency has a version that matches a specification.
  // If either the plugin version or specification are undefined, then they're considered compatible.
  static checkVersionCompatibility(id, version, expectedVersion) {
    if (!version || !expectedVersion) {
      return;
    }
    // must satisfy the constraint
    if (!satisfies(version, expectedVersion)) {
      throw new Error(`Found dependency '${String(id)}' but version is not compatible (needed ${expectedVersion} but found ${version})`);
    }
  }

  // verify that the given dependency has a version that matches a specification.
  // If either the plugin version or specification are undefined, then they're considered compatible.
  static checkCompatibility(dependency, expectedVersion) {
    PluginInitializer.checkVersionCompatibility(dependency.id, dependency.version, expectedVersion);
  }
  addProvided(providedPlugins) {
    this.providedPlugins = {
      ...this.providedPlugins,
      ...providedPlugins
    };
  }

  // topological sort of plugins, putting them in an order
  // where plugins are loaded after their dependencies.
  sortAndCheck(plugins) {
    const byId = new Map();
    plugins.forEach(plugin => byId.set(plugin.id, plugin));
    const ordered = [];
    const loader = this;
    const found = new Set();
    function visit(plugin) {
      let compatible = true;
      for (const [id, version] of normalizeDependencies(plugin.dependsOn)) {
        try {
          const dependency = byId.get(id);
          if (!dependency) {
            // also check to see if it's already been loaded
            const alreadyLoaded = loader.initializedPlugins.get(id);
            if (alreadyLoaded) {
              PluginInitializer.checkCompatibility(alreadyLoaded, version);
            } else {
              const providedVersion = loader.providedPlugins[id];
              if (providedVersion) {
                PluginInitializer.checkVersionCompatibility(id, providedVersion, version);
              } else {
                throw new Error(`Plugin '${String(plugin.id)}' depends on '${id}' but ${id} not provided or already loaded`);
              }
            }
          } else {
            PluginInitializer.checkCompatibility(dependency, version);
            visit(dependency);
          }
        } catch (e) {
          compatible = false;
          PluginInitializer.LOGGER.error(ErrorUtils.getErrorMsgFromObj(e));
          loader.incompatiblePlugins.set(plugin.id, plugin);
        }
      }
      if (compatible && !found.has(plugin.id)) {
        found.add(plugin.id);
        ordered.push(plugin);
      }
    }

    // visit plugins in order, sorting out dependencies
    plugins.forEach(visit);
    return ordered;
  }
}
_defineProperty(PluginInitializer, "LOGGER", LogUtils.getLogger(__filename));
export const initializer = new PluginInitializer();
//# sourceMappingURL=Plugins.js.map
